import { hidratarLG } from "../styles/liquidGlass";
import type { TiendaBaseConfigDTO } from "./types";

let _logoFolder = "cream";
let _logoExt: { favicon: "svg" | "png"; pill: "svg" | "png"; stacked: "svg" | "png" } = {
    favicon: "svg",
    pill: "svg",
    stacked: "svg",
};

export function getLogoAssets() {
    return {
        favicon: `/images/logo/${_logoFolder}/favicon.${_logoExt.favicon}`,
        pill: `/images/logo/${_logoFolder}/pill.${_logoExt.pill}`,
        stacked: `/images/logo/${_logoFolder}/stacked.${_logoExt.stacked}`,
    };
}

async function probeLogoExt(folder: string, name: string): Promise<"svg" | "png"> {
    const exists = await new Promise<boolean>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.naturalWidth > 0);
        img.onerror = () => resolve(false);
        img.src = `/images/logo/${folder}/${name}.svg?probe=${Date.now()}`;
    });
    return exists ? "svg" : "png";
}

async function aplicarAssets(config: TiendaBaseConfigDTO): Promise<void> {
    _logoFolder = config.identidad.vchLogoFolder ?? "cream";

    const [favExt, pillExt, stackedExt] = await Promise.all([
        probeLogoExt(_logoFolder, "favicon"),
        probeLogoExt(_logoFolder, "pill"),
        probeLogoExt(_logoFolder, "stacked"),
    ]);
    _logoExt = { favicon: favExt, pill: pillExt, stacked: stackedExt };

    const link = document.querySelector<HTMLLinkElement>(
        'link[rel="icon"][type="image/svg+xml"], link[rel="icon"][type="image/png"]',
    );
    if (link) {
        link.href = `/images/logo/${_logoFolder}/favicon.${favExt}`;
        link.type = favExt === "svg" ? "image/svg+xml" : "image/png";
    }
}

export function aplicarTemplateTheme(config: TiendaBaseConfigDTO): void {
    if (typeof document === "undefined") return;

    const { theme, identidad } = config;
    const root = document.documentElement;

    const vars: Record<string, string> = {
        "--template-name": `"${config.vchTemplateId}"`,
        "--tenant-name": `"${identidad.vchNombreTienda}"`,
        "--font-family": theme.tipografia.vchFontFamilyBase,
        "--font-family-base": theme.tipografia.vchFontFamilyBase,
        "--font-family-heading": theme.tipografia.vchFontFamilyTitulos,
        // Paleta de fondos (cream-*) y textos (ink-*)
        "--cream-50": theme.colores.vchFondoPagina,
        "--cream-100": theme.colores.vchFondoPagina,
        "--cream-200": theme.colores.vchFondoPaginaSecundario,
        "--cream-300": theme.colores.vchFondoSuperficie,
        "--cream-400": theme.colores.vchFondoSuperficieElevada,
        "--ink-900": theme.colores.vchTextoPrincipal,
        "--ink-800": theme.colores.vchTextoPrincipal,
        "--ink-700": theme.colores.vchTextoSecundario,
        "--ink-500": theme.colores.vchTextoMuted,
        "--ink-400": theme.colores.vchTextoMuted,
        "--ink-300": theme.inputs.vchDisabled,
        // Marca
        "--gold": theme.colores.vchMarcaPrincipal,
        "--gold-soft": theme.colores.vchMarcaSecundaria,
        // Superficies glass
        "--glass-bg": theme.cards.vchFondo,
        "--glass-bg-strong": theme.colores.vchFondoSuperficieElevada,
        "--glass-bg-dark": theme.colores.vchOverlay,
        "--appbar-bg": theme.header.vchFondo,
        "--menu-bg": theme.colores.vchFondoSuperficie,
        "--surface-hover": theme.colores.vchSeleccion,
        // Botones
        "--btn-dark-bg": theme.botones.vchPrimaryBg,
        "--btn-dark-text": theme.botones.vchPrimaryTexto,
        // Bordes y divisores
        "--border-color": theme.colores.vchBorde,
        "--border-color-strong": theme.colores.vchBordeFuerte,
        "--border-dark-color": theme.colores.vchBordeFuerte,
        "--divider": theme.colores.vchDivider,
        "--glass-border": `1px solid ${theme.colores.vchBorde}`,
        "--glass-border-strong": `1px solid ${theme.colores.vchBordeFuerte}`,
        "--glass-border-dark": `1px solid ${theme.colores.vchBordeFuerte}`,
        "--glass-border-in": "inset 0 1px 0 rgba(255,255,255,0.35)",
        // Radios
        "--radius-xs": `${theme.radios.intXs}px`,
        "--radius-sm": `${theme.radios.intSm}px`,
        "--radius-md": `${theme.radios.intMd}px`,
        "--radius-lg": `${theme.radios.intLg}px`,
        "--radius-xl": `${theme.radios.intXl}px`,
        "--radius-pill": `${theme.radios.intPill}px`,
        // Blur
        "--blur-normal": theme.blur.vchNormal,
        "--blur-light": theme.blur.vchNormal,
        "--blur-strong": theme.blur.vchFuerte,
        // Sombras
        "--shadow-sm": theme.sombras.vchSm,
        "--shadow-md": theme.sombras.vchMd,
        "--shadow-lg": theme.sombras.vchLg,
        "--shadow-xl": theme.sombras.vchLg,
        "--glass-shadow": theme.sombras.vchMd,
        "--glass-shadow-lg": theme.sombras.vchLg,
        "--glass-shadow-dark": theme.sombras.vchLg,
        // Gradientes
        "--page-gradient-1": theme.gradientes.vchPagina,
        "--page-gradient-2": theme.gradientes.vchHero,
        // Semánticos adicionales
        "--color-bg": theme.colores.vchFondoPagina,
        "--color-surface": theme.colores.vchFondoSuperficie,
        "--color-primary": theme.colores.vchMarcaPrincipal,
        "--color-secondary": theme.colores.vchMarcaSecundaria,
        "--color-text": theme.colores.vchTextoPrincipal,
        "--color-text-muted": theme.colores.vchTextoMuted,
        "--color-border": theme.colores.vchBorde,
        "--space-base": `${theme.espaciado.intBase}px`,
        "--space-section": `${theme.espaciado.intSeccion}px`,
    };

    Object.entries(vars).forEach(([key, value]) => {
        root.style.setProperty(key, value);
    });

    document.title = identidad.vchNombreTienda;
    document.body.style.fontFamily = theme.tipografia.vchFontFamilyBase;
    document.body.style.color = theme.colores.vchTextoPrincipal;
    document.body.style.backgroundColor = theme.colores.vchFondoPagina;
    document.body.style.margin = "0";
    document.body.style.padding = "0";

    // Sincroniza el objeto LG en memoria para componentes que lo usan directamente
    hidratarLG(config);

    // Logo assets (async, fire-and-forget)
    aplicarAssets(config);
}
